import React, { useState, useEffect, useRef } from "react";
import Header from "../Header";
import Footer from "../Footer";
import API_BASE_URL from "../../Config";
import { useNavigate } from "react-router-dom";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";

/* ══════════════════════════════════════════════
   SVG ICON SYSTEM
══════════════════════════════════════════════ */
const Icon = ({ d, size = 18, stroke = 1.8, fill = "none" }) => (
  <svg
    width={size} height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const SearchIcon    = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></>}/>;
const ChevronDown   = (p) => <Icon {...p} d={<path d="m6 9 6 6 6-6"/>}/>;
const CheckIcon     = (p) => <Icon {...p} d={<path d="M20 6 9 17l-5-5"/>}/>;
const CloseIcon     = (p) => <Icon {...p} d={<path d="M18 6 6 18M6 6l12 12"/>}/>;
const UploadIcon    = (p) => <Icon {...p} d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>}/>;
const GripIcon      = (p) => <Icon {...p} d={<><circle cx="9" cy="5" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="9" cy="19" r="1"/><circle cx="15" cy="19" r="1"/></>}/>;
const CheckCircle   = (p) => <Icon {...p} d={<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>}/>;
const SaveIcon      = (p) => <Icon {...p} d={<><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></>}/>;
const TagIcon       = (p) => <Icon {...p} d={<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></>}/>;
const PackageIcon   = (p) => <Icon {...p} d={<><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></>}/>;
const DollarIcon    = (p) => <Icon {...p} d={<><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>}/>;
const ImageIcon     = (p) => <Icon {...p} d={<><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></>}/>;
const InfoIcon      = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></>}/>;
const ArrowLeft     = (p) => <Icon {...p} d={<><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></>}/>;

/* ══════════════════════════════════════════════
   SEARCHABLE SELECT COMPONENT
══════════════════════════════════════════════ */
const SearchableSelect = ({ options = [], value, onChange, placeholder = "Select…", id, error }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const ref = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  // Calculate dropdown position relative to viewport for portal-like behavior
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 280; // approximate max height

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        // Open upward
        setDropdownStyle({
          position: "fixed",
          top: "auto",
          bottom: window.innerHeight - rect.top + 6,
          left: rect.left,
          width: rect.width,
          zIndex: 99999,
        });
      } else {
        // Open downward
        setDropdownStyle({
          position: "fixed",
          top: rect.bottom + 6,
          left: rect.left,
          width: rect.width,
          zIndex: 99999,
        });
      }
    }
  }, [open]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 280;

        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          setDropdownStyle((prev) => ({
            ...prev,
            top: "auto",
            bottom: window.innerHeight - rect.top + 6,
            left: rect.left,
            width: rect.width,
          }));
        } else {
          setDropdownStyle((prev) => ({
            ...prev,
            top: rect.bottom + 6,
            bottom: "auto",
            left: rect.left,
            width: rect.width,
          }));
        }
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );
  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <div className="pa-ss-wrap" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className={`pa-ss-trigger ${open ? "open" : ""} ${error ? "has-error" : ""}`}
        onClick={() => { setOpen((o) => !o); setQuery(""); }}
        id={id}
      >
        <span className={selected ? "pa-ss-selected" : "pa-ss-placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={15} />
      </button>
      {open && (
        <div className="pa-ss-dropdown" style={dropdownStyle}>
          <div className="pa-ss-search">
            <SearchIcon size={14} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="pa-ss-list">
            <div
              className={`pa-ss-option ${!value ? "active" : ""}`}
              onClick={() => { onChange(""); setOpen(false); setQuery(""); }}
            >
              <span className="pa-ss-opt-label">— None —</span>
            </div>
            {filtered.length === 0 && (
              <div className="pa-ss-empty">No results found</div>
            )}
            {filtered.map((opt) => (
              <div
                key={opt.value}
                className={`pa-ss-option ${String(value) === String(opt.value) ? "active" : ""}`}
                onClick={() => { onChange(opt.value); setOpen(false); setQuery(""); }}
              >
                {String(value) === String(opt.value) && <CheckIcon size={13} stroke={2.5} />}
                <span className="pa-ss-opt-label">{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   IMAGE UPLOAD & DRAG-REORDER
══════════════════════════════════════════════ */
const ImageUploader = ({ images, onChange }) => {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const addFiles = (files) => {
    const newImgs = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
    }));
    onChange([...images, ...newImgs]);
  };

  const remove = (id) => {
    onChange(images.filter((img) => img.id !== id));
  };

  const onDragStart = (idx) => { dragItem.current = idx; };
  const onDragEnter = (idx) => { dragOverItem.current = idx; };
  const onDragEnd = () => {
    const copy = [...images];
    const dragged = copy.splice(dragItem.current, 1)[0];
    copy.splice(dragOverItem.current, 0, dragged);
    dragItem.current = null;
    dragOverItem.current = null;
    onChange(copy);
  };

  return (
    <div className="pa-img-uploader">
      <div
        className={`pa-img-dropzone ${dragOver ? "over" : ""}`}
        onClick={() => fileRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
      >
        <UploadIcon size={32} />
        <p>Drop images here or <span>browse files</span></p>
        <small>Supports PNG, JPG, WEBP · Multiple files allowed</small>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>
      {images.length > 0 && (
        <div className="pa-img-grid">
          {images.map((img, idx) => (
            <div
              key={img.id}
              className="pa-img-thumb"
              draggable
              onDragStart={() => onDragStart(idx)}
              onDragEnter={() => onDragEnter(idx)}
              onDragEnd={onDragEnd}
            >
              {idx === 0 && <span className="pa-img-primary-tag">Primary</span>}
              <img src={img.preview || `${API_BASE_URL}public/productimages/${img.file}`} alt="" />
              <div className="pa-img-overlay">
                <span className="pa-img-grip"><GripIcon size={14} /></span>
                <button type="button" className="pa-img-remove" onClick={(e) => { e.stopPropagation(); remove(img.id); }}>
                  <CloseIcon size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {images.length > 0 && (
        <p className="pa-img-hint">
          <GripIcon size={13} /> Drag images to reorder. First image becomes the primary product image.
        </p>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   SUCCESS POPUP
══════════════════════════════════════════════ */
const SuccessPopup = ({ onClose, productName }) => (
  <div className="pa-popup-overlay" onClick={onClose}>
    <div className="pa-popup-box" onClick={(e) => e.stopPropagation()}>
      <div className="pa-popup-icon">
        <CheckCircle size={48} stroke={1.5} />
      </div>
      <h3>Product Created!</h3>
      <p><b>{productName}</b> has been added to your catalogue successfully.</p>
      <button className="pa-popup-btn" onClick={onClose}>
        Continue
      </button>
    </div>
  </div>
);

/* ══════════════════════════════════════════════
   FORM FIELD COMPONENTS
══════════════════════════════════════════════ */
const FormField = ({ label, required, error, children, hint }) => (
  <div className="pa-field">
    <label className="pa-label">
      {label}{required && <span className="pa-required">*</span>}
    </label>
    {children}
    {hint && !error && <p className="pa-hint">{hint}</p>}
    {error && <p className="pa-error-msg">{error}</p>}
  </div>
);

/* ══════════════════════════════════════════════
   SECTION CARD
══════════════════════════════════════════════ */
const SectionCard = ({ icon, title, subtitle, children }) => (
  <div className="pa-section-card">
    <div className="pa-section-head">
      <div className="pa-section-icon">{icon}</div>
      <div>
        <h3 className="pa-section-title">{title}</h3>
        {subtitle && <p className="pa-section-sub">{subtitle}</p>}
      </div>
    </div>
    <div className="pa-section-body">{children}</div>
  </div>
);

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const ProductAdd = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successName, setSuccessName] = useState("");
  const [howToUse, setHowToUse] = useState("");


const [faq, setFAQ] = useState([
  { question: "", answer: "" }
]);

const addFAQ = () => {
  setFAQ([
    ...faq,
    { question: "", answer: "" }
  ]);
};

const removeFAQ = (index) => {
  setFAQ(faq.filter((_, i) => i !== index));
};

const updateFAQ = (index, field, value) => {
  const updated = [...faq];
  updated[index][field] = value;
  setFAQ(updated);
};

  // 🔹 Product state
  const [product, setProduct] = useState({
    name: "",
    sku: "",
    category: "",
    subcategory: "",
    medicine_type: "",
    generic_name: "",
    status: "Active",
    brand: "",
    model: "",
    weight: "",
    color: "",
    releaseDate: "",
    description: "",
    features: "",
    price: "",
    dprice: "",
    stock: "",
    minOrderQuantity: "",
    backorderAllowed: "No",
    offer: "",
    ingredients: "",
  });

  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [medicineType, setMedicineType] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});

  // 🔹 Handle input
  const handleChange = (e) => {
    const { id, value } = e.target;
    setProduct({ ...product, [id]: value });
    if (id === "category") {
      setProduct((prev) => ({ ...prev, category: value, subcategory: "" }));
      fetchSubcategories(value);
    }
    // Clear error on change
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  // 🔹 Fetch categories
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/categories_front`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status) setCategories(data.data);
    } catch (error) { console.error("Error fetching categories:", error); }
  };

  const fetchMedicineType = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/medicinetype`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status) setMedicineType(data.data);
    } catch (error) { console.error("Error fetching medicine types:", error); }
  };

  const fetchModel = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/models`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status) setModels(data.data);
    } catch (error) { console.error("Error fetching models:", error); }
  };

  const fetchBrands = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/brands`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status) setBrands(data.data);
    } catch (error) { console.error("Error fetching brands:", error); }
  };

  // 🔹 Fetch subcategories
  const fetchSubcategories = async (categoryId) => {
    if (!categoryId) { setSubcategories([]); return; }
    try {
      const res = await fetch(`${API_BASE_URL}api/subcategories/category/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status) setSubcategories(data.data);
    } catch (error) { console.error("Error fetching subcategories:", error); }
  };

  useEffect(() => {
    fetchCategories();
    fetchBrands();
    fetchModel();
    fetchMedicineType();
  }, []);

  const validateForm = () => {
    let newErrors = {};
    if (!product.category) newErrors.category = "Category is required";
    if (!product.generic_name.trim()) newErrors.generic_name = "Generic name is required";
    if (!product.name.trim()) newErrors.name = "Product name is required";
    if (!product.brand) newErrors.brand = "Brand is required";
    if (!product.model) newErrors.model = "Model is required";
    if (!product.medicine_type) newErrors.medicine_type = "Medicine type is required";
    if (!product.price) newErrors.price = "Price is required";
    else if (product.price <= 0) newErrors.price = "Price must be greater than 0";
    if (!product.stock) newErrors.stock = "Stock is required";
    else if (product.stock < 0) newErrors.stock = "Stock cannot be negative";
    if (!product.description || product.description === "<p><br></p>") {
      newErrors.description = "Product description is required";
    }
    // if (!product.ingredients || product.ingredients === "<p><br></p>") {
    //   newErrors.ingredients = "Ingredients are required";
    // }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setLoading(true);
    const formData = new FormData();
    Object.keys(product).forEach((key) => formData.append(key, product[key]));
    images.forEach((img, i) => formData.append(`images[${i}]`, img.file));
 formData.append("how_to_use", howToUse);


formData.append(
  "faq",
  JSON.stringify(faq)
);
    try {
      const response = await fetch(`${API_BASE_URL}api/products`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (data.status) {
        setSuccessName(product.name);
        setShowSuccess(true);
      } else {
        setMessage("❌ Failed to add product. Please try again.");
      }
    } catch (error) {
      console.error(error);
      setMessage("⚠️ Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    navigate("/product");
  };

  // Options for searchable selects
  const categoryOptions = categories.map((c) => ({ value: c.id, label: c.name }));
  const subcategoryOptions = subcategories.map((s) => ({ value: s.id, label: s.name }));
  const brandOptions = brands.map((b) => ({ value: b.id, label: b.name }));
  const modelOptions = models.map((m) => ({ value: m.id, label: m.name }));
  const medicineTypeOptions = medicineType.map((m) => ({ value: m.id, label: m.name }));

  return (
    <>
      {/* ══════════ STYLES ══════════ */}
      <style>{`
        :root {
          --pa-bg:           #f3faf6;
          --pa-card:         #ffffff;
          --pa-border:       #e3f0e9;
          --pa-text:         #0f1f1a;
          --pa-muted:        #6b7a74;
          --pa-primary:      #10b981;
          --pa-primary-d:    #059669;
          --pa-primary-grad: linear-gradient(135deg, #14b8a6 0%, #10b981 100%);
          --pa-soft:         #ecfdf5;
          --pa-del-bg:       #fee4e2;
          --pa-del-fg:       #e0413c;
          --pa-shadow:       0 1px 2px rgba(16,30,24,.04), 0 4px 16px rgba(16,30,24,.06);
          --pa-shadow-lg:    0 8px 40px rgba(16,30,24,.14);
          --pa-radius:       16px;
          --pa-input-h:      44px;
        }

        /* ── Shell ── */
        .pa-shell { background: var(--pa-bg); min-height: 100%; padding: 24px; }

        /* ── Page Header ── */
        .pa-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 24px; }
        .pa-head-left h2 { font-size: clamp(20px, 2.2vw, 28px); font-weight: 800; color: var(--pa-text); margin: 0 0 4px; letter-spacing: -0.01em; }
        .pa-head-left p { color: var(--pa-muted); font-size: 13.5px; margin: 0; }
        .pa-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--pa-muted); flex-wrap: wrap; }
        .pa-breadcrumb span { color: var(--pa-primary-d); font-weight: 600; }
        .pa-breadcrumb-sep { opacity: .4; }

        /* ── Back button ── */
        .pa-back-btn {
          display: inline-flex; align-items: center; gap: 6px;
          background: #fff; border: 1.5px solid var(--pa-border);
          padding: 9px 16px; border-radius: 12px;
          font-size: 13.5px; font-weight: 600; color: var(--pa-muted);
          cursor: pointer; transition: all .15s; font-family: inherit;
          text-decoration: none;
        }
        .pa-back-btn svg { fill: none !important; }
        .pa-back-btn:hover { border-color: var(--pa-primary); color: var(--pa-primary-d); background: var(--pa-soft); }

        /* ── Layout ── */
        .pa-layout { display: grid; grid-template-columns: 1fr 340px; gap: 20px; align-items: start; }
        .pa-main-col { display: flex; flex-direction: column; gap: 20px; }
        .pa-side-col { display: flex; flex-direction: column; gap: 20px; position: sticky; top: 24px; }

        /* ── Section Card ── */
        .pa-section-card {
          background: var(--pa-card);
          border: 1px solid var(--pa-border);
          border-radius: var(--pa-radius);
          box-shadow: var(--pa-shadow);
          overflow: visible; /* FIX: was 'hidden' — caused dropdown clipping */
        }
        .pa-section-head {
          display: flex; align-items: center; gap: 12px;
          padding: 18px 22px;
          background: linear-gradient(135deg, #f0fbf6 0%, #e8f8f0 100%);
          border-bottom: 1px solid var(--pa-border);
          border-radius: var(--pa-radius) var(--pa-radius) 0 0; /* keep rounded top corners */
        }
        .pa-section-icon {
          width: 38px; height: 38px; border-radius: 10px;
          background: #2F4A3A;
          display: grid; place-items: center;
          color: #fff; flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(16,185,129,.28);
        }
        .pa-section-icon svg { fill: none !important; }
        .pa-section-title { font-size: 15px; font-weight: 800; color: var(--pa-text); margin: 0 0 2px; }
        .pa-section-sub { font-size: 12.5px; color: var(--pa-muted); margin: 0; }
        .pa-section-body { padding: 22px; }

        /* ── Grid ── */
        .pa-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
        .pa-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 18px; }
        .pa-col-span-2 { grid-column: span 2; }
        .pa-col-span-3 { grid-column: span 3; }

        /* ── Field ── */
        .pa-field { display: flex; flex-direction: column; gap: 6px; }
        .pa-label { font-size: 12px; font-weight: 700; color: #3d4a45; letter-spacing: .05em; text-transform: uppercase; }
        .pa-required { color: #e0413c; margin-left: 3px; }
        .pa-hint { font-size: 12px; color: var(--pa-muted); margin: 0; }
        .pa-error-msg { font-size: 12px; color: #e0413c; margin: 0; font-weight: 600; display: flex; align-items: center; gap: 4px; }

        /* ── Inputs ── */
        .pa-input, .pa-select {
          height: var(--pa-input-h);
          width: 100%;
          padding: 0 14px;
          border-radius: 11px;
          border: 1.5px solid var(--pa-border);
          background: #f9fcfb;
          font-size: 14px;
          color: var(--pa-text);
          outline: none;
          transition: all .15s;
          font-family: inherit;
          appearance: none;
        }
        .pa-input::placeholder { color: #9db5aa; }
        .pa-input:focus, .pa-select:focus {
          border-color: var(--pa-primary);
          background: #fff;
          box-shadow: 0 0 0 4px rgba(16,185,129,.12);
        }
        .pa-input.has-error, .pa-select.has-error {
          border-color: #e0413c;
          background: #fff8f8;
        }
        .pa-input.has-error:focus, .pa-select.has-error:focus {
          box-shadow: 0 0 0 4px rgba(224,65,60,.12);
        }

        /* custom select arrow */
        .pa-select-wrap { position: relative; }
        .pa-select-wrap::after {
          content: "";
          pointer-events: none;
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          width: 0; height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 5px solid var(--pa-muted);
        }
        .pa-select { padding-right: 36px; cursor: pointer; }

        /* ── Searchable Select ── */
        .pa-ss-wrap { position: relative; width: 100%; }
        .pa-ss-trigger {
          width: 100%; height: var(--pa-input-h);
          display: flex; align-items: center; justify-content: space-between; gap: 8px;
          padding: 0 14px; border-radius: 11px; border: 1.5px solid var(--pa-border);
          background: #f9fcfb; font-size: 14px; color: var(--pa-text);
          cursor: pointer; transition: all .15s; font-family: inherit; text-align: left;
        }
        .pa-ss-trigger.open, .pa-ss-trigger:hover { border-color: var(--pa-primary); background: #fff; box-shadow: 0 0 0 4px rgba(16,185,129,.10); }
        .pa-ss-trigger.has-error { border-color: #e0413c; background: #fff8f8; }
        .pa-ss-trigger svg { flex-shrink: 0; fill: none !important; color: var(--pa-muted); }
        .pa-ss-placeholder { color: #9db5aa; font-size: 14px; }
        .pa-ss-selected { color: var(--pa-text); font-weight: 600; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .pa-ss-dropdown {
          /* Position is set inline via JS (fixed positioning relative to viewport) */
          background: #fff;
          border: 1px solid var(--pa-border);
          border-radius: 14px;
          box-shadow: var(--pa-shadow-lg);
          animation: paDropIn .18s ease;
          overflow: hidden;
        }
        @keyframes paDropIn { from { opacity:0; transform: translateY(-6px); } to { opacity:1; transform: translateY(0); } }
        .pa-ss-search {
          display: flex; align-items: center; gap: 8px; padding: 10px 12px;
          border-bottom: 1px solid #eef4f1;
        }
        .pa-ss-search svg { fill: none !important; color: #9db5aa; flex-shrink: 0; }
        .pa-ss-search input { flex: 1; border: none; outline: none; font-size: 13.5px; color: var(--pa-text); background: transparent; font-family: inherit; }
        .pa-ss-list { max-height: 220px; overflow-y: auto; padding: 6px; }
        .pa-ss-list::-webkit-scrollbar { width: 4px; }
        .pa-ss-list::-webkit-scrollbar-thumb { background: #d6efe2; border-radius: 99px; }
        .pa-ss-option {
          display: flex; align-items: center; gap: 8px; padding: 9px 10px;
          border-radius: 9px; cursor: pointer; font-size: 13.5px; font-weight: 500;
          color: var(--pa-text); transition: background .13s; user-select: none;
        }
        .pa-ss-option:hover { background: var(--pa-soft); }
        .pa-ss-option.active { background: #f0fbf6; color: var(--pa-primary-d); font-weight: 700; }
        .pa-ss-option svg { fill: none !important; color: var(--pa-primary); flex-shrink: 0; }
        .pa-ss-opt-label { flex: 1; }
        .pa-ss-empty { padding: 16px; text-align: center; color: var(--pa-muted); font-size: 13px; }

        /* ── Image Uploader ── */
        .pa-img-uploader { display: flex; flex-direction: column; gap: 12px; }
        .pa-img-dropzone {
          border: 2px dashed var(--pa-border); border-radius: 14px; padding: 32px 20px;
          text-align: center; cursor: pointer; background: #f6fbf8;
          transition: all .2s; color: var(--pa-muted);
        }
        .pa-img-dropzone:hover, .pa-img-dropzone.over {
          border-color: var(--pa-primary); background: #ecfdf5; color: var(--pa-primary-d);
        }
        .pa-img-dropzone svg { margin: 0 auto 10px; display: block; fill: none !important; }
        .pa-img-dropzone p { font-size: 14px; font-weight: 600; margin: 0 0 4px; }
        .pa-img-dropzone p span { color: var(--pa-primary); text-decoration: underline; }
        .pa-img-dropzone small { font-size: 12px; }
        .pa-img-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 10px; }
        .pa-img-thumb {
          position: relative; border-radius: 12px; overflow: hidden;
          aspect-ratio: 1; border: 1.5px solid var(--pa-border);
          cursor: grab; background: #f6fbf8; transition: box-shadow .2s;
        }
        .pa-img-thumb:hover { box-shadow: 0 4px 14px rgba(16,30,24,.14); }
        .pa-img-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .pa-img-primary-tag {
          position: absolute; top: 5px; left: 5px; z-index: 2;
          background: var(--pa-primary); color: #fff;
          font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 999px;
          letter-spacing: .03em;
        }
        .pa-img-overlay {
          position: absolute; inset: 0; background: rgba(15,31,26,.45);
          display: flex; align-items: flex-end; justify-content: space-between;
          padding: 6px; opacity: 0; transition: opacity .18s;
        }
        .pa-img-thumb:hover .pa-img-overlay { opacity: 1; }
        .pa-img-grip { color: #fff; display: grid; place-items: center; cursor: grab; }
        .pa-img-grip svg { fill: none !important; }
        .pa-img-remove {
          width: 24px; height: 24px; border-radius: 6px; border: none;
          background: var(--pa-del-bg); color: var(--pa-del-fg); cursor: pointer;
          display: grid; place-items: center; padding: 0; transition: background .14s;
        }
        .pa-img-remove svg { fill: none !important; }
        .pa-img-remove:hover { background: #fdd1cf; }
        .pa-img-hint { font-size: 12px; color: var(--pa-muted); display: flex; align-items: center; gap: 6px; margin: 0; }
        .pa-img-hint svg { fill: none !important; }

        /* ── Submit Button ── */
        .pa-submit-btn {
          width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px;
          background: #2F4A3A; color: #fff; border: none;
          padding: 14px 24px; font-weight: 800; font-size: 15px;
          border-radius: 14px; cursor: pointer;
          box-shadow: 0 6px 20px rgba(16,185,129,.32);
          transition: transform .15s, box-shadow .2s, filter .2s;
          font-family: inherit; letter-spacing: .01em;
        }
        .pa-submit-btn svg { fill: none !important; }
        .pa-submit-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(16,185,129,.4); filter: brightness(1.06); }
        .pa-submit-btn:active { transform: translateY(0); }
        .pa-submit-btn:disabled { opacity: .6; cursor: not-allowed; transform: none; }

        .pa-cancel-btn {
          width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px;
          background: #fff; color: var(--pa-muted); border: 1.5px solid var(--pa-border);
          padding: 13px 24px; font-weight: 600; font-size: 14px;
          border-radius: 14px; cursor: pointer;
          transition: all .15s; font-family: inherit;
        }
        .pa-cancel-btn svg { fill: none !important; }
        .pa-cancel-btn:hover { border-color: var(--pa-primary); color: var(--pa-primary-d); background: var(--pa-soft); }

        /* ── Error banner ── */
        .pa-error-banner {
          padding: 14px 18px; border-radius: 12px;
          background: #fff1f0; border: 1px solid #fbd7d3;
          color: #991b1b; font-size: 13.5px; font-weight: 600;
          display: flex; align-items: center; gap: 10px;
          animation: paDropIn .25s ease; margin-bottom: 20px;
        }
        .pa-error-banner svg { fill: none !important; flex-shrink: 0; }

        /* ── Status Card ── */
        .pa-status-card {
          background: var(--pa-card);
          border: 1px solid var(--pa-border);
          border-radius: var(--pa-radius);
          box-shadow: var(--pa-shadow);
          overflow: visible; /* FIX: ensure side column dropdowns also not clipped */
          padding: 18px;
          display: flex; flex-direction: column; gap: 14px;
        }
        .pa-status-title { font-size: 13px; font-weight: 800; color: var(--pa-text); text-transform: uppercase; letter-spacing: .05em; margin-bottom: 2px; }
        .pa-toggle-wrap { display: flex; align-items: center; gap: 10px; }
        .pa-toggle {
          position: relative; width: 44px; height: 24px; flex-shrink: 0;
        }
        .pa-toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
        .pa-toggle-track {
          position: absolute; inset: 0; border-radius: 999px; cursor: pointer;
          background: #d1d5db; transition: background .2s;
        }
        .pa-toggle input:checked + .pa-toggle-track { background: var(--grad-main); }
        .pa-toggle-track::before {
          content: ""; position: absolute;
          width: 18px; height: 18px; border-radius: 50%;
          background: #fff; top: 3px; left: 3px;
          transition: transform .2s;
          box-shadow: 0 1px 4px rgba(0,0,0,.18);
        }
        .pa-toggle input:checked + .pa-toggle-track::before { transform: translateX(20px); }
        .pa-toggle-label { font-size: 13.5px; font-weight: 600; color: var(--pa-text); }
        .pa-toggle-sub { font-size: 12px; color: var(--pa-muted); }
        .pa-divider { height: 1px; background: var(--pa-border); margin: 2px 0; }

        /* ── Backorder select ── */
        .pa-badge-group { display: flex; gap: 8px; }
        .pa-badge-opt {
          flex: 1; padding: 8px 12px; border-radius: 10px;
          border: 1.5px solid var(--pa-border); background: #f9fcfb;
          font-size: 13px; font-weight: 600; color: var(--pa-muted);
          cursor: pointer; text-align: center; transition: all .15s; font-family: inherit;
        }
        .pa-badge-opt.selected { border-color: var(--pa-primary); background: var(--pa-soft); color: var(--pa-primary-d); }

        /* ── Loading Overlay ── */
        .pa-overlay {
          position: fixed; inset: 0; z-index: 1080; background: rgba(255,255,255,.55);
          backdrop-filter: blur(2px); display: grid; place-items: center;
        }
        .pa-spinner {
          width: 46px; height: 46px; border-radius: 50%;
          border: 3px solid #d6efe2; border-top-color: var(--pa-primary);
          animation: paspin 0.8s linear infinite;
        }
        @keyframes paspin { to { transform: rotate(360deg); } }

        /* ── Success Popup ── */
        .pa-popup-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(10,25,20,.5); backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center; padding: 20px;
          animation: paFadeIn .2s ease;
        }
        @keyframes paFadeIn { from { opacity:0; } to { opacity:1; } }
        .pa-popup-box {
          background: var(--pa-card); border-radius: 24px;
          width: 100%; max-width: 400px; padding: 40px 32px;
          text-align: center; box-shadow: var(--pa-shadow-lg);
          animation: paSlideUp .3s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes paSlideUp { from { opacity:0; transform: translateY(30px) scale(.95); } to { opacity:1; transform: translateY(0) scale(1); } }
        .pa-popup-icon {
          width: 80px; height: 80px; margin: 0 auto 20px;
          border-radius: 50%; background: var(--pa-soft);
          display: grid; place-items: center; color: var(--pa-primary);
        }
        .pa-popup-icon svg { fill: none !important; }
        .pa-popup-box h3 { font-size: 22px; font-weight: 800; color: var(--pa-text); margin: 0 0 10px; }
        .pa-popup-box p { color: var(--pa-muted); font-size: 14px; margin: 0 0 28px; line-height: 1.6; }
        .pa-popup-btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          background: var(--pa-primary-grad); color: #fff; border: none;
          padding: 13px 36px; border-radius: 14px; font-size: 15px; font-weight: 700;
          cursor: pointer; box-shadow: 0 6px 20px rgba(16,185,129,.3);
          transition: all .15s; font-family: inherit;
        }
        .pa-popup-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(16,185,129,.4); filter: brightness(1.05); }

        /* ── Required fields note ── */
        .pa-required-note { font-size: 12px; color: var(--pa-muted); display: flex; align-items: center; gap: 4px; }
        .pa-required-note b { color: #e0413c; }

        /* ── SunEditor override ── */
        .sun-editor { border-radius: 11px !important; border: 1.5px solid var(--pa-border) !important; overflow: hidden; }
        .sun-editor .se-toolbar { background: #f6fbf8 !important; border-bottom: 1px solid var(--pa-border) !important; }
        .sun-editor.has-error { border-color: #e0413c !important; }

        /* ── Responsive ── */
        @media (max-width: 1024px) {
          .pa-layout { grid-template-columns: 1fr; }
          .pa-side-col { position: static; }
        }
        @media (max-width: 768px) {
          .pa-shell { padding: 14px; }
          .pa-grid-2 { grid-template-columns: 1fr; }
          .pa-grid-3 { grid-template-columns: 1fr 1fr; }
          .pa-col-span-2 { grid-column: span 1; }
          .pa-col-span-3 { grid-column: span 2; }
          .pa-head { flex-direction: column; }
        }
        @media (max-width: 480px) {
          .pa-grid-3 { grid-template-columns: 1fr; }
          .pa-col-span-3 { grid-column: span 1; }
        }
      `}</style>

      <div id="layout-wrapper">
        <Header />

        {loading && (
          <div className="pa-overlay">
            <div className="pa-spinner" />
          </div>
        )}

        {showSuccess && (
          <SuccessPopup productName={successName} onClose={handleSuccessClose} />
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="pa-shell">

              {/* ── Page Header ── */}
              <div className="pa-head">
                <div className="pa-head-left">
                  <h2>Create Product</h2>
                  <div className="pa-breadcrumb">
                    <span style={{ opacity: .5 }}>Ecommerce</span>
                    <span className="pa-breadcrumb-sep">›</span>
                    <span>Products</span>
                    <span className="pa-breadcrumb-sep">›</span>
                    <span>Create New</span>
                  </div>
                </div>
                <button type="button" className="pa-back-btn" onClick={() => navigate("/product")}>
                  <ArrowLeft size={15} />
                  Back to Products
                </button>
              </div>

              {/* ── Error Banner ── */}
              {message && (
                <div className="pa-error-banner">
                  <InfoIcon size={18} />
                  {message}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="pa-layout">

                  {/* ══ MAIN COLUMN ══ */}
                  <div className="pa-main-col">

                    {/* Category Info */}
                    <SectionCard
                      icon={<TagIcon size={18} />}
                      title="Category Info"
                      subtitle="Classify your product within the catalogue"
                    >
                      <div className="pa-grid-2">
                        <FormField label="Category" required error={errors.category}>
                          <SearchableSelect
                            id="category"
                            options={categoryOptions}
                            value={product.category}
                            onChange={(val) => {
                              setProduct((prev) => ({ ...prev, category: val, subcategory: "" }));
                              fetchSubcategories(val);
                              if (errors.category) setErrors((prev) => ({ ...prev, category: "" }));
                            }}
                            placeholder="Select a category…"
                            error={errors.category}
                          />
                        </FormField>

                        <FormField label="Subcategory" hint="Optional — select after choosing a category">
                          <SearchableSelect
                            id="subcategory"
                            options={subcategoryOptions}
                            value={product.subcategory}
                            onChange={(val) => setProduct((prev) => ({ ...prev, subcategory: val }))}
                            placeholder={subcategories.length ? "Select subcategory…" : "Select a category first"}
                          />
                        </FormField>

                        <FormField label="Common Name" required error={errors.generic_name} className="pa-col-span-2">
                          <input
                            id="generic_name"
                            type="text"
                            className={`pa-input ${errors.generic_name ? "has-error" : ""}`}
                            placeholder="e.g. Paracetamol, Amoxicillin…"
                            value={product.generic_name}
                            onChange={handleChange}
                          />
                        </FormField>
                      </div>
                    </SectionCard>

                    {/* General Info */}
                    <SectionCard
                      icon={<PackageIcon size={18} />}
                      title="General Info"
                      subtitle="Core product details and specifications"
                    >
                      <div className="pa-grid-3">
                        <FormField label="Product Name" required error={errors.name}>
                          <input
                            id="name"
                            type="text"
                            className={`pa-input ${errors.name ? "has-error" : ""}`}
                            placeholder="Enter product name"
                            value={product.name}
                            onChange={handleChange}
                          />
                        </FormField>

                        <FormField label="SKU">
                          <input
                            id="sku"
                            type="text"
                            className="pa-input"
                            placeholder="e.g. MED-001"
                            value={product.sku}
                            onChange={handleChange}
                          />
                        </FormField>

                        <FormField label="Brand" required error={errors.brand}>
                          <SearchableSelect
                            id="brand"
                            options={brandOptions}
                            value={product.brand}
                            onChange={(val) => {
                              setProduct((prev) => ({ ...prev, brand: val }));
                              if (errors.brand) setErrors((prev) => ({ ...prev, brand: "" }));
                            }}
                            placeholder="Select brand…"
                            error={errors.brand}
                          />
                        </FormField>

                        <FormField label="Model" required error={errors.model}>
                          <SearchableSelect
                            id="model"
                            options={modelOptions}
                            value={product.model}
                            onChange={(val) => {
                              setProduct((prev) => ({ ...prev, model: val }));
                              if (errors.model) setErrors((prev) => ({ ...prev, model: "" }));
                            }}
                            placeholder="Select model…"
                            error={errors.model}
                          />
                        </FormField>

                        <FormField label="Medicine Type" required error={errors.medicine_type}>
                          <SearchableSelect
                            id="medicine_type"
                            options={medicineTypeOptions}
                            value={product.medicine_type}
                            onChange={(val) => {
                              setProduct((prev) => ({ ...prev, medicine_type: val }));
                              if (errors.medicine_type) setErrors((prev) => ({ ...prev, medicine_type: "" }));
                            }}
                            placeholder="Select type…"
                            error={errors.medicine_type}
                          />
                        </FormField>

                        <FormField label="Weight" hint="In grams or kg">
                          <input
                            id="weight"
                            type="number"
                            className="pa-input"
                            placeholder="e.g. 250"
                            value={product.weight}
                            onChange={handleChange}
                          />
                        </FormField>

                        <FormField label="Color">
                          <input
                            id="color"
                            type="text"
                            className="pa-input"
                            placeholder="e.g. White, Blue"
                            value={product.color}
                            onChange={handleChange}
                          />
                        </FormField>

                        <FormField label="Release Date">
                          <input
                            id="releaseDate"
                            type="date"
                            className="pa-input"
                            value={product.releaseDate}
                            onChange={handleChange}
                          />
                        </FormField>
                      </div>
                    </SectionCard>

                    {/* Description */}
                    <SectionCard
                      icon={<InfoIcon size={18} />}
                      title="Description"
                      subtitle="Detailed product information for customers"
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                        <FormField label="Product Description" required error={errors.description}>
                          <div className={errors.description ? "has-error" : ""}>
                            <SunEditor
                              setContents={product.description}
                              setOptions={{
                                height: 260,
                                buttonList: [
                                  ["undo", "redo"],
                                  ["bold", "italic", "underline"],
                                  ["list", "align"],
                                  ["link", "image"],
                                  ["codeView"]
                                ]
                              }}
                              onChange={(content) => {
                                setProduct((prev) => ({ ...prev, description: content }));
                                if (errors.description) setErrors((prev) => ({ ...prev, description: "" }));
                              }}
                            />
                          </div>
                        </FormField>

                        <FormField label="Manufacturer Details" hint="Additional info about the manufacturer">
                          <SunEditor
                            setContents={product.features}
                            setOptions={{
                              height: 200,
                              buttonList: [
                                ["undo", "redo"],
                                ["bold", "italic", "underline"],
                                ["list", "align"],
                                ["link", "image"],
                                ["codeView"]
                              ]
                            }}
                            onChange={(content) =>
                              setProduct((prev) => ({ ...prev, features: content }))
                            }
                          />
                        </FormField>

                        <FormField
                            label="Ingredients"
                            
                            error={errors.ingredients}
                            hint="Enter product ingredients or composition details"
                          >
                            <SunEditor
                              setContents={product.ingredients}
                              setOptions={{
                                height: 200,
                                buttonList: [
                                  ["undo", "redo"],
                                  ["bold", "italic", "underline"],
                                  ["list", "align"],
                                  ["link", "image"],
                                  ["codeView"]
                                ]
                              }}
                              onChange={(content) =>
                                setProduct((prev) => ({
                                  ...prev,
                                  ingredients: content
                                }))
                              }
                            />
                          </FormField>

                      </div>
                    </SectionCard>

                      <SectionCard
                        icon={<InfoIcon size={18} />}
                        title="How To Use"
                        subtitle="Add usage instructions for this product"
                      >
                        <SunEditor
                          setContents={howToUse}
                          setOptions={{
                            height: 220,
                            buttonList: [
                              ["undo", "redo"],
                              ["bold", "italic", "underline"],
                              ["list", "align"],
                              ["link"],
                              ["codeView"]
                            ]
                          }}
                          onChange={(content) => setHowToUse(content)}
                        />
                      </SectionCard>
                    <SectionCard
                          icon={<InfoIcon size={18} />}
                          title="FAQ"
                          subtitle="Add usage instructions as question and answer"
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                            {faq.map((item, index) => (
                              <div
                                key={index}
                                style={{
                                  border: "1px solid #e3f0e9",
                                  borderRadius: "12px",
                                  padding: "16px",
                                  background: "#f9fcfb"
                                }}
                              >
                                <div className="pa-grid-2">

                                  <FormField label={`Question ${index + 1}`}>
                                    <input
                                      type="text"
                                      className="pa-input"
                                      placeholder="Enter question"
                                      value={item.question}
                                      onChange={(e) =>
                                        updateFAQ(index, "question", e.target.value)
                                      }
                                    />
                                  </FormField>

                                  <FormField label={`Answer ${index + 1}`}>
                                    <textarea
                                      className="pa-input"
                                      rows="4"
                                      style={{ height: "100px", paddingTop: "10px" }}
                                      placeholder="Enter answer"
                                      value={item.answer}
                                      onChange={(e) =>
                                        updateFAQ(index, "answer", e.target.value)
                                      }
                                    />
                                  </FormField>

                                </div>

                                {faq.length > 1 && (
                                  <button
                                    type="button"
                                    className="pa-cancel-btn"
                                    style={{ marginTop: "10px" }}
                                    onClick={() => removeFAQ(index)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            ))}

                            <button
                              type="button"
                              className="pa-submit-btn"
                              onClick={addFAQ}
                            >
                              + Add Question & Answer
                            </button>
                          </div>
                        </SectionCard>

                    {/* Pricing & Stock */}
                    <SectionCard
                      icon={<DollarIcon size={18} />}
                      title="Pricing & Stock"
                      subtitle="Set pricing, discount, and inventory levels"
                    >
                      <div className="pa-grid-3">
                        <FormField label="Price (₹)" required error={errors.price}>
                          <input
                            id="price"
                            type="number"
                            className={`pa-input ${errors.price ? "has-error" : ""}`}
                            placeholder="0.00"
                            value={product.price}
                            onChange={handleChange}
                          />
                        </FormField>

                        <FormField label="Discount (%)" hint="Percentage off the price">
                          <input
                            id="dprice"
                            type="number"
                            className="pa-input"
                            placeholder="0"
                            value={product.dprice}
                            onChange={handleChange}
                          />
                        </FormField>

                        {/* <FormField label="Offer" hint="Special offer value">
                          <input
                            id="offer"
                            type="number"
                            className="pa-input"
                            placeholder="0"
                            value={product.offer}
                            onChange={handleChange}
                          />
                        </FormField> */}

                        <FormField label="Stock Quantity" required error={errors.stock}>
                          <input
                            id="stock"
                            type="number"
                            className={`pa-input ${errors.stock ? "has-error" : ""}`}
                            placeholder="0"
                            value={product.stock}
                            onChange={handleChange}
                          />
                        </FormField>

                        <FormField label="Min. Order Quantity" hint="Minimum units per order">
                          <input
                            id="minOrderQuantity"
                            type="number"
                            className="pa-input"
                            placeholder="1"
                            value={product.minOrderQuantity}
                            onChange={handleChange}
                          />
                        </FormField>

                        {/* <FormField label="Backorder Allowed">
                          <div className="pa-badge-group">
                            {["No", "Yes"].map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                className={`pa-badge-opt ${product.backorderAllowed === opt ? "selected" : ""}`}
                                onClick={() => setProduct((prev) => ({ ...prev, backorderAllowed: opt }))}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </FormField> */}
                      </div>
                    </SectionCard>

                    {/* Images */}
                    <SectionCard
                      icon={<ImageIcon size={18} />}
                      title="Product Images"
                      subtitle="Upload and arrange product photos"
                    >
                      <ImageUploader images={images} onChange={setImages} />
                    </SectionCard>
                  </div>

                  {/* ══ SIDE COLUMN ══ */}
                  <div className="pa-side-col">

                    {/* Publish Card */}
                    <div className="pa-status-card">
                      <div>
                        <p className="pa-status-title">Publish Settings</p>
                      </div>

                      <div>
                        <p className="pa-label" style={{ marginBottom: 10 }}>Status</p>
                        <div className="pa-toggle-wrap">
                          <label className="pa-toggle">
                            <input
                              type="checkbox"
                              checked={product.status === "Active"}
                              onChange={(e) =>
                                setProduct((prev) => ({ ...prev, status: e.target.checked ? "Active" : "InActive" }))
                              }
                            />
                            <span className="pa-toggle-track"></span>
                          </label>
                          <div>
                            <p className="pa-toggle-label">{product.status === "Active" ? "Active" : "Inactive"}</p>
                            <p className="pa-toggle-sub">{product.status === "Active" ? "Visible in catalogue" : "Hidden from catalogue"}</p>
                          </div>
                        </div>
                      </div>

                      <div className="pa-divider" />

                      <div className="pa-required-note">
                        <b>*</b> Required fields must be filled
                      </div>

                      <button type="submit" className="pa-submit-btn" disabled={loading}>
                        <SaveIcon size={17} />
                        {loading ? "Creating Product…" : "Create Product"}
                      </button>

                      <button
                        type="button"
                        className="pa-cancel-btn"
                        onClick={() => navigate("/product")}
                      >
                        Cancel
                      </button>
                    </div>

                    {/* Quick Summary Card */}
                    <div className="pa-status-card">
                      <p className="pa-status-title">Quick Summary</p>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {[
                          { label: "Name", val: product.name || "—" },
                          { label: "SKU", val: product.sku || "—" },
                          { label: "Price", val: product.price ? `₹${product.price}` : "—" },
                          { label: "Stock", val: product.stock || "—" },
                          { label: "Images", val: images.length > 0 ? `${images.length} uploaded` : "None" },
                        ].map(({ label, val }) => (
                          <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 12, color: "var(--pa-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</span>
                            <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--pa-text)", maxWidth: 160, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default ProductAdd;